/***
 * File name: fbnerMixin.js
 *
 * Program Description:
 * This file contains all the reusable data and functions for all FBNER related
 * components.
 *
 * Includes:
 * - Functions that fetch initial data needed for the fields within the FBNER Forms
 * - Validations for the action button and forms restricting unauthorized users
 * - File upload and download functionalities
 *
 */
import crud from '@/utils/rest/crud'
import fields from '@/meta/activityLogs'
import { mapGetters } from 'vuex'
import { dataURLToFile, toDateString } from '@/utils/helpers'
import { format } from 'date-fns'
import { validationMixin, validationErrors } from '@/utils/validations'
export default {
  name: 'fbnerMixin',
  mixins: [validationMixin],
  data: () => ({
    loading: true,
    dbnID: '',
    dateUpdated: '',
    isSubmitted: false,
    notifiedDataSubject: '',
    reasonNotNotifiedDataSubject: '',
    endorsedTo: '',
    endorseToUser: '',
    drafts: [],
    users: [],
    notifTypeItems: [],
    viewDocData: undefined,
    formItem: {
      docsArray: [],
      actionTakenArray: [
        {
          actionTaken: '',
          date: '',
          timeliness: ''
        }
      ],
      violations: [],
      requestMade: [],
      adjudicationFileUpload: [],
      enforcementFileUpload: [],
      investigationFileUpload: []
    },
    natureOfBreach: [],
    submittedNature: [],
    docsRequired: 1,
    file: null,
    assignedTo: {},
    currentStatus: '',
    readOnly: true,
    activeTab: 1,
    forSubmission: false,
    dbnStatusList: [],
    isDraft: false,
    dialog: false,
    datesToFormat: [
      'dateOfSubmission',
      'dateOfIssuance'
    ],
    objToFlat: [
      'organizationalSecurityMeasures',
      'physicalSecurityMeasures',
      'technicalSecurityMeasures',
      'measuresToAddressBreach',
      'measuresToSecurePersonalData',
      'actionsToMitigateHarm',
      'actionsToInformDataSubjects',
      'measuresToPreventRecurrence',
      'statusOfCompliance',
      'recommendation',
      'representative'
    ],
    legalBasisOptions: [
      'Section 17(D), NPC Circular 2016-03',
      'Section 4, NPC Circular 2016-03',
      'Section 20 (c)(3), DPA',
      'Section 20, DPA',
      'Section 20 (c), DPA',
      'Section 20 (a), (b), DPA',
      'Section 20 (c)(1), DPA',
      'Section 20 (e), DPA',
      'Section 20 (a), DPA',
      'Section 20 (d), DPA',
      'Section 18, NPC Circular 2016-03',
      'Section 20 (f), DPA',
      'Section 17(A), (B), (C), NPC Circular 2016-03'
    ],
    datesComponents: [
      'dateOfDSNotification',
      'dateOfOccurrence',
      'initialDateOfCompliance',
      'fullDateOfCompliance'
    ],
    objNameChange: [
      {
        typeOfNotification: 'notificationType'
      },
      {
        docsArray: 'documentsRequired'
      },
      {
        actionTakenArray: 'timelineOfActionsTaken'
      },
      {
        consequences: 'likelyConsequences'
      }
    ],
    registrationOptions: [
      {
        _id: 'Registered',
        name: 'Registered'
      },
      {
        _id: 'Not Registered',
        name: 'Not Registered'
      }
    ],
    isIncidentInvolveOptions: [
      {
        _id: 'Yes',
        name: 'Yes'
      },
      {
        _id: 'No',
        name: 'No'
      }
    ],
    exemptionOptions: [
      {
        _id: 'Yes',
        name: 'Yes'
      },
      {
        _id: 'No',
        name: 'No'
      }
    ],
    notifiedDataSubjOptions: [
      {
        _id: 'Yes',
        name: 'Yes'
      },
      {
        _id: 'No',
        name: 'No'
      }
    ],
    notificationTypeOptions: [
      {
        _id: 'Voluntary',
        name: 'Voluntary'
      },
      {
        _id: 'Mandatory',
        name: 'Mandatory'
      }
    ]
  }),
  computed: {
    ...validationErrors,
    ...mapGetters('lookups', {
      profile: 'getCurrentUser'
    }),
    pdbnfCollection () {
      return crud('pdbnf')
    },
    fbnerCollection () {
      return crud('fbner')
    },
    dbnStatusCrud () {
      return crud('dbnStatus')
    },
    id () {
      return this.$route.params.id
    },
    operation () {
      return this.$route.params.operation
    },
    collection () {
      return this.$route.params.dataTab
    },
    roleView () {
      return this.$route.params.role
    },
    isPic () {
      return this.$s.userRole.includes('pic')
    },
    isRecipient () {
      return this.$s.userRole.includes('adjudicationRecipient') || this.$s.userRole.includes('enforcementRecipient') || this.$s.userRole.includes('investigationRecipient')
    },
    isNotified () {
      const { dateOfDSNotification, modeOfDSNotification, assistanceProvided } = this.formItem
      return dateOfDSNotification || modeOfDSNotification || assistanceProvided
    },
    dateOfFbnerSubmission () {
      if (!(this.formItem && this.formItem.statusHistory)) return ''
      const fbner = this.formItem.statusHistory.find(item => item.status === 'For Evaluation - FBNER')
      return stringValue => stringValue ? toDateString(fbner.date) : fbner.date
    },
    isPdbnfValid () {
      if (typeof this.formItem.pdbnf !== 'object') return this.formItem._status === 'active'
      return this.formItem.pdbnf && this.formItem.pdbnf._status === 'active'
    },
    dateOfIssuance () {
      if (!(this.formItem && this.formItem.statusHistory)) return ''
      const fbner = [...this.formItem.statusHistory].reverse().find(item => item.status === 'Initial Order')
      return toDateString(fbner.date)
    },
    hasShowCauseOrder () {
      if (!(this.formItem && this.formItem.statusHistory)) return 'No'
      const hasShowCauseOrder = this.formItem.statusHistory.find(item => item.status === 'Show Cause Order')
      return hasShowCauseOrder ? 'Yes' : 'No'
    },
    toDateString () {
      return date => toDateString(date)
    },
    toYearMonthDay () {
      return date => format(new Date(date), 'yyyy-MM-dd')
    },
    noPossibleDpaViolation () {
      return this.formItem.possibleDpaViolation && this.formItem.possibleDpaViolation.includes('None')
    },
    enableUpload () {
      const enabledStatus = this.currentStatus.value === 'For Adjudication' || this.currentStatus.value === 'For Investigation' || this.currentStatus.value === 'For Enforcement' || this.currentStatus.value === 'Closed'
      return enabledStatus
    },
    firstUploadField () {
      if (this.formItem.investigationFileUpload.size) {
        return 'Investigation'
      } else {
        if (this.currentStatus.value !== 'For Investigation') {
          return 'Adjudication'
        } else {
          return 'Investigation'
        }
      }
    },

    secondUploadField () {
      if (this.formItem.investigationFileUpload.size) {
        return 'Adjudication'
      } else {
        if (this.currentStatus.value !== 'For Investigation') {
          return 'Enforcement'
        } else {
          return 'Adjudication'
        }
      }
    },

    viewFirstUpload () {
      if (this.firstUploadField === 'Adjudication') {
        if (this.currentStatus.value === 'For Adjudication') {
          return this.assignedAdjudicator
        } else {
          return true
        }
      } if (this.firstUploadField === 'Investigation') {
        if (this.currentStatus.value === 'For Investigation') {
          return this.assignedInvestigator
        } else {
          return true
        }
      }
    },
    viewSecondUpload () {
      if (this.secondUploadField === 'Adjudication') {
        if (this.currentStatus.value === 'For Adjudication') {
          return this.assignedAdjudicator
        } else {
          return true
        }
      } if (this.secondUploadField === 'Enforcement') {
        if (this.currentStatus.value === 'For Enforcement') {
          return this.assignedEnforcer
        } else {
          return true
        }
      }
    },
    viewThirdUpload () {
      if (this.secondUploadField === 'Adjudication') {
        if (this.currentStatus.value === 'For Enforcement') {
          return this.assignedEnforcer
        } else {
          return true
        }
      }
    },
    canViewUploadHeader () {
      if (this.currentStatus.value === 'For Investigation') {
        return this.assignedInvestigator
      } if (this.currentStatus.value === 'For Adjudication' && this.firstUploadField === 'Adjudication') {
        return this.assignedAdjudicator
      }
      return true
    },
    assignedAdjudicator () {
      const adjudication = this.dbnStatusList.find(x => x.value === 'For Adjudication')
      const status = this.currentStatus.value || (this.formItem.status && this.formItem.status.value)
      const isAssigned = this.$s.user === this.formItem.endorserId
      return isAssigned && status === (adjudication && adjudication.value)
    },
    assignedInvestigator () {
      const investigation = this.dbnStatusList.find(x => x.value === 'For Investigation')
      const status = this.currentStatus.value || (this.formItem.status && this.formItem.status.value)
      const isAssigned = this.$s.user === this.formItem.endorserId
      return isAssigned && status === (investigation && investigation.value)
    },
    assignedEnforcer () {
      const enforcement = this.dbnStatusList.find(x => x.value === 'For Enforcement')
      const status = this.currentStatus.value || (this.formItem.status && this.formItem.status.value)
      const isAssigned = this.$s.user === this.formItem.endorserId
      return isAssigned && status === (enforcement && enforcement.value)
    },
    canAddReviewedBy () {
      return this.currentStatus.value === 'Initial Review - FBNER' || this.currentStatus.value === 'Final Review - FBNER'
    },
    canAddApprovedBy () {
      return this.currentStatus.value === 'Final Review - FBNER'
    },
    errorMessages () {
      return ({ field, message }) => {
        const hasErrors = this.genericError(`formItem.${field}`, '').length > 0
        if (hasErrors) return [message]
        return []
      }
    },

    schema () {
      return {
        status: this.formItem.status,
        pdbnf: this.formItem.pdbnf,
        isSubmitted: this.isSubmitted,
        nameOfPic: this.formItem.organization,
        dbnNumber: this.formItem.dbnNumber,
        dateOfOccurrence: this.formItem.dateOfOccurrence,
        timeOfOccurrence: this.formItem.timeOfOccurrence,
        dateOfDiscovery: this.formItem.dateOfDiscovery,
        timeOfDiscovery: this.formItem.timeOfDiscovery,
        dateOfNotification: this.formItem.dateOfNotification,
        exemptionToDSNotification: this.formItem.exemptionToDSNotification,
        justificationForExemption: this.formItem.justificationForExemption,
        dateOfDSNotification: this.formItem.dateOfDSNotification,
        modeOfDSNotification: this.formItem.modeOfDSNotification,
        assistanceProvided: this.formItem.assistanceProvided,
        dateOfSubmission: this.formItem.dateOfSubmission,
        registration: this.formItem.registration,
        isIncidentInvolve: this.formItem.isIncidentInvolve,
        incidentInvolve: this.formItem.incidentInvolve,
        requestMade: this.formItem.requestMade,
        specifyIfOthers: this.formItem.specifyIfOthers,
        summary: this.formItem.summary,
        notificationType: this.formItem.typeOfNotification,
        notifiedDataSubject: this.formItem.notifiedDataSubject,
        reasonNotNotifiedDataSubject: this.formItem.reasonNotNotifiedDataSubject,
        requisites: this.formItem.requisites,
        timeliness: this.formItem.timeliness,
        justified: this.formItem.justified,
        remarksTimeliness: this.formItem.remarksTimeliness,
        generalCause: this.formItem.generalCause,
        specificCause: this.formItem.specificCause,
        remarksCause: this.formItem.remarksCause,
        natureOfBreach: this.submittedNature.length ? this.submittedNature : this.formItem.natureOfBreach,
        numberOfAffectedDataRecords: this.formItem.numberOfAffectedDataRecords,
        sensitivePersonalInformation: this.formItem.sensitivePersonalInfo,
        remarksBreach: this.formItem.remarksBreach,
        otherInfo: this.formItem.otherInfo,
        remarksOtherInfo: this.formItem.remarksOtherInfo,
        likelyConsequences: this.formItem.consequences,
        remarksConsequences: this.formItem.remarksConsequences,
        organizationalSecurityMeasures: {
          findings: this.formItem.organizationalFindings,
          referencesOrSupportingEvidence: this.formItem.organizationalReferences,
          recommendations: this.formItem.organizationalRecommendations
        },
        physicalSecurityMeasures: {
          findings: this.formItem.physicalFindings,
          referencesOrSupportingEvidence: this.formItem.physicalReferences,
          recommendations: this.formItem.physicalRecommendations
        },
        technicalSecurityMeasures: {
          findings: this.formItem.technicalFindings,
          referencesOrSupportingEvidence: this.formItem.technicalReferences,
          recommendations: this.formItem.technicalRecommendations
        },
        measuresToAddressBreach: {
          findings: this.formItem.addressFindings,
          referencesOrSupportingEvidence: this.formItem.addressReferences,
          recommendations: this.formItem.addressRecommendations
        },
        measuresToSecurePersonalData: {
          findings: this.formItem.secureFindings,
          referencesOrSupportingEvidence: this.formItem.secureReferences,
          recommendations: this.formItem.secureRecommendations
        },
        actionsToMitigateHarm: {
          findings: this.formItem.mitigateFindings,
          referencesOrSupportingEvidence: this.formItem.mitigateReferences,
          recommendations: this.formItem.mitigateRecommendations
        },
        actionsToInformDataSubjects: {
          findings: this.formItem.informFindings,
          referencesOrSupportingEvidence: this.formItem.informReferences,
          recommendations: this.formItem.informRecommendations
        },
        measuresToPreventRecurrence: {
          findings: this.formItem.preventFindings,
          referencesOrSupportingEvidence: this.formItem.preventReferences,
          recommendations: this.formItem.preventRecommendations
        },
        summaryOfObservationsAndFindings: this.formItem.summaryOfObservationsAndFindings,
        recommendedAction: this.formItem.recommendedAction,
        remarksRecommendation: this.formItem.remarksRecommendation,
        possibleDpaViolation: this.formItem.possibleDpaViolation,
        remarksDpaViolation: this.formItem.remarksDpaViolation,
        nameOfDpo: this.formItem.nameOfDpo,
        officeAddress: this.formItem.officeAddress,
        emailAddress: this.formItem.emailAddress,
        mobileOrOfficeNumber: this.formItem.mobileOrOfficeNumber,
        representative: {
          name: this.formItem.representativeName,
          officeAddress: this.formItem.representativeAddress,
          emailAddress: this.formItem.representativeEmail,
          mobileOrOfficeNumber: this.formItem.representativeNumber
        },
        statusOfCompliance: {
          initialReport: {
            statusOfCompliance: this.formItem.initialStatusOfCompliance,
            dateOfCompliance: this.formItem.initialDateOfCompliance
          },
          fullReport: {
            statusOfCompliance: this.formItem.fullStatusOfCompliance,
            dateOfCompliance: this.formItem.fullDateOfCompliance
          }
        },
        typeOfIssuance: this.formItem.typeOfIssuance,
        dateOfIssuance: this.formItem.dateOfIssuance,
        documentsRequired: this.formItem.docsArray,
        recommendation: {
          overall: this.formItem.overall,
          recommendation: this.formItem.overallRecommendation,
          remarks: this.formItem.overallRemarks
        },
        violations: this.formItem.violations,
        specifyIfSec25: this.formItem.specifyIfSec25,
        specifyIfSec26: this.formItem.specifyIfSec26,
        specifyIfSec27: this.formItem.specifyIfSec27,
        specifyIfSec28: this.formItem.specifyIfSec28,
        specifyIfSec29: this.formItem.specifyIfSec29,
        specifyIfSec30: this.formItem.specifyIfSec30,
        specifyIfSec31: this.formItem.specifyIfSec31,
        specifyIfSec32: this.formItem.specifyIfSec32,
        timelineOfActionsTaken: this.formItem.actionTakenArray,
        evaluatedBy: this.isSubmitted ? this.formItem.evaluatedBy : null,
        reviewedBy: this.canAddReviewedBy ? this.formItem.reviewedBy : null,
        approvedBy: this.canAddApprovedBy ? this.formItem.approvedBy : null,
        endorserId: this.formItem.endorserId
      }
    }
  },
  async beforeCreate () {
    const data = await crud('dbnStatus').list(0, 100)
    this.dbnStatusList = data.entries
  },
  async created () {
    if (this.roleView === 'default' || this.roleView === 'drafts') {
      await this.getDbnData()
      await this.getDrafts()
      this.autogenerateFields()
    } else {
      await this.viewFbner()
    }
    await this.getUsers()
    await this.getNatureOfBreach()
    await this.getNotificationTypes()
    if (this.operation !== 'view') this.readOnly = false
    this.loading = false
  },
  methods: {
    async getDbnData () {
      const data = await this.pdbnfCollection.read(this.id)
      this.dbnID = data.entry._id
      this.assignedTo = data.entry.assignedTo
      this.populateUserData(data.entry)
      const keys = Object.keys(data.entry)
      await keys.forEach((field) => {
        if (field === 'pic') {
          this.$set(this.formItem, 'pic', data.entry.pic._id)
        } else if (this.objToFlat.includes(field) && data.entry[`${field}`]) {
          this.flattenObj(field, data.entry[`${field}`])
        } else if (this.datesComponents.includes(field) && data.entry[`${field}`]) {
          this.$set(this.formItem, field, this.toYearMonthDay(data.entry[`${field}`]))
        } else if (field === 'dateOfDiscovery') {
          this.$set(this.formItem, 'dateOfDiscovery', format(new Date(data.entry[`${field}`]), 'MMMM d yyyy'))
        } else if (field === 'dateOfNotification') {
          this.$set(this.formItem, 'dateOfNotification', format(new Date(data.entry[`${field}`]), 'MMMM d yyyy'))
        } else {
          this.$set(this.formItem, field, data.entry[`${field}`])
        }
      })
      let notifiedValue = null
      if (!data.entry.notifiedDataSubject && this.isNotified) notifiedValue = 'Yes'
      this.$set(this.formItem, 'notifiedDataSubject', notifiedValue || data.entry.notifiedDataSubject)
    },
    async getNotificationTypes () {
      const advancedQuery = { type: 'notificationType' }
      const { entries } = await crud('notificationType').list({ advancedQuery, count: 10000 })
      this.notifTypeItems = entries
    },
    async getDrafts (id) {
      const advancedQuery = { isSubmitted: { $eq: false }, 'pdbnf._id': { $eq: id || this.id } }
      const list = await crud('fbner').list({ advancedQuery })
      this.drafts = list.entries
    },
    async getNatureOfBreach () {
      const list = await crud('natureOfBreach').list()
      this.natureOfBreach = list.entries
    },
    async getUsers () {
      let response = null
      let counter = 1
      do {
        response = await crud('admin/users').list({
          count: 100000 * counter
        })
        counter++
      } while (response && response.ok && (response.entries.length < response.total))

      this.users = response.entries
    },

    async viewFbner () {
      const data = await this.fbnerCollection.read(this.id)
      await this.populateUserData(data.entry)
      const keys = Object.keys(data.entry)
      await keys.forEach((field) => {
        if (this.objToFlat.includes(field)) {
          data.entry[`${field}`] && this.flattenObj(field, data.entry[`${field}`])
        } else if (this.datesToFormat.includes(field)) {
          this.$set(this.formItem, field, toDateString(data.entry[`${field}`]))
        } else if (this.datesComponents.includes(field) && data.entry[`${field}`]) {
          this.$set(this.formItem, field, this.toYearMonthDay(data.entry[`${field}`]))
        } else if (field === 'dateOfDiscovery') {
          this.$set(this.formItem, 'dateOfDiscovery', format(new Date(data.entry[`${field}`]), 'MMMM d yyyy'))
        } else if (field === 'dateOfNotification') {
          this.$set(this.formItem, 'dateOfNotification', format(new Date(data.entry[`${field}`]), 'MMMM d yyyy'))
        } else if (field === 'documentsRequired') {
          const docsArray = data.entry[`${field}`]
          docsArray.forEach(x => {
            x.dateOfCompliance = x.dateOfCompliance && this.toYearMonthDay(x.dateOfCompliance)
          })
          this.$set(this.formItem, 'docsArray', data.entry[`${field}`])
        } else if (field === 'timelineOfActionsTaken') {
          const actionTakenArray = data.entry[`${field}`]
          actionTakenArray.forEach(x => {
            x.date = x.date && this.toYearMonthDay(x.date)
          })
          if (data.entry.status) {
            const status = this.dbnStatusList.find(status => status._id === data.entry.status)
            if (status) {
              const history = this.formItem.pdbnf.statusHistory.find(item => item.status === status.value)
              const value = {
                actionTaken: history.status,
                date: history.date && this.toYearMonthDay(history.date),
                timeliness: ''
              }
              const allowedStatus = ['Initial Review - FBNER', 'Final Review - FBNER']
              if (allowedStatus.includes(status.value)) actionTakenArray.push(value)
            }
          }
          this.$set(this.formItem, 'actionTakenArray', actionTakenArray)
        } else if (field === 'notificationType') {
          this.$set(this.formItem, 'typeOfNotification', data.entry[`${field}`])
        } else if (field === 'sensitivePersonalInformation') {
          this.$set(this.formItem, 'sensitivePersonalInfo', data.entry[`${field}`])
        } else if (field === 'likelyConsequences') {
          this.$set(this.formItem, 'consequences', data.entry[`${field}`])
        } else {
          this.$set(this.formItem, field, data.entry[`${field}`])
        }
      }
      )

      const dbnData = await this.pdbnfCollection.read(this.formItem.pdbnf._id)
      this.assignedTo = dbnData.entry.assignedTo
      this.currentStatus = dbnData.entry.status
      this.isDraft = !data.entry.isSubmitted
      let notifiedValue = null
      if (!data.entry.notifiedDataSubject && this.isNotified) notifiedValue = 'Yes'
      this.$set(this.formItem, 'notifiedDataSubject', notifiedValue || data.entry.notifiedDataSubject)
      this.$set(this, 'viewDocData', dbnData.entry)
      this.loading = false
      if (this.currentStatus.value === 'Initial Review - FBNER') {
        this.$set(this.formItem, 'reviewedBy', this.assignedTo.firstPass._id)
      }
      if (this.currentStatus.value === 'Final Review - FBNER') {
        this.$set(this.formItem, 'approvedBy', this.assignedTo.final._id)
      }
    },
    async getStatusList () {
      const data = await this.dbnStatusCrud.list(0, 100)
      this.dbnStatusList = data.entries
    },
    async populateUserData (entry) {
      const $pic = entry.pic || entry.picObj
      this.$set(this.formItem, 'dbnNumber', entry.dbnNumber)
      this.$set(this.formItem, 'organization', entry.nameOfPic || $pic.organization)
      this.$set(this.formItem, 'dateOfDiscovery', format(new Date(entry.dateOfDiscovery), 'MMMM dd yyyy'))
      this.$set(this.formItem, 'dateOfNotification', format(new Date(entry.dateOfNotification), 'MMMM dd yyyy'))
      this.$set(this.formItem, 'timeliness', entry.timeliness)
      this.$set(this.formItem, 'justified', 'entry.justified')
      this.$set(this.formItem, 'generalCause', entry.generalCause)
      this.$set(this.formItem, 'specificCause', entry.specificCause)
      this.$set(this.formItem, 'nameOfDpo', `${$pic.firstName} ${$pic.lastName}`)
      this.$set(this.formItem, 'emailAddress', $pic.email)
      this.$set(this.formItem, 'mobileOrOfficeNumber', $pic.contactNumber)
      this.$set(this.formItem, 'specificCause', entry.specificCause)
      this.$set(this.formItem, 'pdbnf', entry._id)
      let requisite
      if (this.roleView !== 'evaluate' && this.roleView !== 'review') {
        requisite = entry.notificationType.map((x) => {
          return x.value
        })
        this.$set(this.formItem, 'requisites', requisite)
      } else {
        this.$set(this.formItem, 'requisites', entry.notificationType)
      }
    },
    autogenerateFields () {
      const dateOfSubmission = this.toYearMonthDay(this.dateOfFbnerSubmission(false))
      this.$set(this.formItem, 'dateOfSubmission', this.dateOfFbnerSubmission(true))
      this.$set(this.formItem, 'numberOfAffectedDataRecords', this.formItem.numberOfDataSubjectsAffected)
      this.$set(this.formItem, 'requestMade', this.formItem.typeOfRequest)
      this.$set(this.formItem, 'specifyIfOthers', this.formItem.typeOfRequestOther)
      this.$set(this.formItem, 'dateOfIssuance', this.dateOfIssuance)
      this.$set(this.formItem, 'sensitivePersonalInfo', this.formItem.sensitivePersonalInformation.details)
      this.$set(this.formItem, 'otherInfo', this.formItem.identityFraud.details)
      this.$set(this.formItem, 'consequences', this.formItem.likelyConsequences.details)
      this.$set(this.formItem, 'summary', this.formItem.briefSummary)
      this.$set(this.formItem, 'justificationForExemption', this.formItem.justificationExemption)
      this.$set(this.formItem, 'initialDateOfCompliance', this.toYearMonthDay(this.formItem.dateOfNotification))
      this.$set(this.formItem, 'fullDateOfCompliance', dateOfSubmission)
      const isExemption = this.formItem.typeOfRequest.includes('Request for exemption') ? 'Yes' : 'No'
      const actionTaken = this.formItem.statusHistory.map(item => {
        const action = item.status === 'For Evaluation - FBNER' ? 'PIC submitted additional document' : item.status
        return {
          actionTaken: action,
          date: this.toYearMonthDay(item.date),
          timeliness: ''
        }
      })
      this.$set(this.formItem, 'actionTakenArray', actionTaken)
      this.$set(this.formItem, 'exemptionToDSNotification', isExemption)
      for (const [key, checked] of Object.entries(this.formItem.enabled)) {
        const doc = fields[`enabled.${key}`].name
        if (checked) this.addDocRow({ doc, dateOfSubmission, disabled: true })
      }
    },
    flattenObj (field, obj) {
      const reference = obj.referencesOrSupportingEvidence
      const isOldData = typeof reference === 'string'
      const referenceValue = isOldData ? [reference] : reference
      if (field === 'organizationalSecurityMeasures') {
        this.$set(this.formItem, 'organizationalFindings', obj.findings)
        this.$set(this.formItem, 'organizationalReferences', referenceValue)
        this.$set(this.formItem, 'organizationalRecommendations', obj.recommendations)
      } else if (field === 'physicalSecurityMeasures') {
        this.$set(this.formItem, 'physicalFindings', obj.findings)
        this.$set(this.formItem, 'physicalReferences', referenceValue)
        this.$set(this.formItem, 'physicalRecommendations', obj.recommendations)
      } else if (field === 'technicalSecurityMeasures') {
        this.$set(this.formItem, 'technicalFindings', obj.findings)
        this.$set(this.formItem, 'technicalReferences', referenceValue)
        this.$set(this.formItem, 'technicalRecommendations', obj.recommendations)
      } else if (field === 'measuresToAddressBreach') {
        this.$set(this.formItem, 'addressFindings', obj.findings)
        this.$set(this.formItem, 'addressReferences', referenceValue)
        this.$set(this.formItem, 'addressRecommendations', obj.recommendations)
      } else if (field === 'measuresToSecurePersonalData') {
        this.$set(this.formItem, 'secureFindings', obj.findings)
        this.$set(this.formItem, 'secureReferences', referenceValue)
        this.$set(this.formItem, 'secureRecommendations', obj.recommendations)
      } else if (field === 'actionsToMitigateHarm') {
        this.$set(this.formItem, 'mitigateFindings', obj.findings)
        this.$set(this.formItem, 'mitigateReferences', referenceValue)
        this.$set(this.formItem, 'mitigateRecommendations', obj.recommendations)
      } else if (field === 'actionsToInformDataSubjects') {
        this.$set(this.formItem, 'informFindings', obj.findings)
        this.$set(this.formItem, 'informReferences', referenceValue)
        this.$set(this.formItem, 'informRecommendations', obj.recommendations)
      } else if (field === 'measuresToPreventRecurrence') {
        this.$set(this.formItem, 'preventFindings', obj.findings)
        this.$set(this.formItem, 'preventReferences', referenceValue)
        this.$set(this.formItem, 'preventRecommendations', obj.recommendations)
      } else if (field === 'statusOfCompliance') {
        const initialDate = obj.initialReport.dateOfCompliance && this.toYearMonthDay(obj.initialReport.dateOfCompliance)
        const finalDate = obj.fullReport.dateOfCompliance && this.toYearMonthDay(obj.fullReport.dateOfCompliance)
        this.$set(this.formItem, 'initialDateOfCompliance', initialDate || obj.initialReport.dateOfCompliance)
        this.$set(this.formItem, 'initialStatusOfCompliance', obj.initialReport.statusOfCompliance)
        this.$set(this.formItem, 'fullDateOfCompliance', finalDate || obj.fullReport.dateOfCompliance)
        this.$set(this.formItem, 'fullStatusOfCompliance', obj.fullReport.statusOfCompliance)
      } else if (field === 'recommendation') {
        this.$set(this.formItem, 'overall', obj.overall)
        this.$set(this.formItem, 'overallRecommendation', obj.recommendation)
        this.$set(this.formItem, 'overallRemarks', obj.remarks)
      } else if (field === 'representative') {
        this.$set(this.formItem, 'representativeName', obj.name)
        this.$set(this.formItem, 'representativeAddress', obj.officeAddress)
        this.$set(this.formItem, 'representativeEmail', obj.emailAddress)
        this.$set(this.formItem, 'representativeNumber', obj.mobileOrOfficeNumber)
      }
    },
    addDocRow ({ doc, dateOfSubmission, disabled }) {
      this.formItem.docsArray.push({
        document: doc || '',
        legalBasisForRequirement: '',
        statusOfCompliance: '',
        dateOfCompliance: dateOfSubmission || '',
        disabled
      })
    },
    addActionTakenRow () {
      this.formItem.actionTakenArray.push({
        actionTaken: '',
        date: '',
        timeliness: ''
      })
    },
    async fileUploadForm (formParams, fieldName) {
      const form = new FormData()
      form.append(fieldName, formParams)
      return form
    },
    async fileUploader (id, field, fieldName) {
      const dataForm = await this.fileUploadForm(field, fieldName)
      let resp = null
      if (dataForm.get(fieldName).size > 0) {
        resp = await crud('attachment/fbner').createForm(
          dataForm,
          {},
          id,
          fieldName
        )
        let errorMsgs = []
        if (!resp.ok) {
          errorMsgs = await Promise.all(resp.map(async (file) => {
            const data = await file.json()
            return data.error
          }))
        }
        return {
          ok: resp.ok,
          error: errorMsgs
        }
      }
    },
    async download (url, file) {
      if (Array.isArray(file)) {
        await Promise.all(file.map((item) => {
          this.fileDownload(url, item)
        }))
      } else this.fileDownload(url, file)
    },
    async fileDownload (url, file) {
      const route = `${url}/${file.fieldname || 'fileUpload'}/${file.filename || file.name}`
      const resp = await crud(route).get()
      if (resp.ok) {
        const data = await resp.json()
        const downloadLink = document.createElement('a')
        downloadLink.href = data.file
        downloadLink.download = file.originalname || file.filename || file.name
        downloadLink.click()
      } else {
        this.localDownload(file)
      }
    },
    async localDownload (file) {
      if (Array.isArray(file)) {
        file.forEach((item) => this.downloadFile(item, item.name))
      } else this.downloadFile(file, file.name)
    },
    downloadFile (blob, name) {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.style.display = 'none'
      link.href = url
      link.download = name
      document.body.appendChild(link)
      link.click()
      window.URL.revokeObjectURL(url)
      link.parentNode.removeChild(link)
    },
    async getImage (id, file) {
      if (!file) return ''
      const getFile = []
      if (Array.isArray(file)) {
        for (let i = 0; i < file.length; i++) {
          this.fetchImage(getFile, id, file[`${i}`])
        }
      } else this.fetchImage(getFile, id, file)
      return getFile
    },
    async fetchImage (getFile, id, file) {
      const { filename, mimetype } = file
      const data = await crud('attachment/fbner').fileUpload(id, filename, {})
      getFile.push(await dataURLToFile(data.file, filename, mimetype))
    }
  }
}
